import React, { useState } from 'react';
import { TrendingUp, Briefcase, Zap, ArrowRight, Check, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface ServicesGridProps {
  openContactForm: () => void;
}
export function ServicesGrid({
  openContactForm
}: ServicesGridProps) {
  const [activeCard, setActiveCard] = useState<number>(0);
  const scrollToCaseStudies = () => {
    const section = document.getElementById('case-studies');
    if (section) {
      section.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const engagements = [{
    id: 0,
    icon: TrendingUp,
    title: 'Fractional CFO & Strategic Advisory',
    oneLiner: 'Better decisions with institutional-grade visibility into cash and profit.',
    outcomes: ['Eliminate cash surprises and regain control of runway', "Know what's driving profit (and what's draining it)", 'Make hiring, pricing, and growth decisions with confidence'],
    expandedContent: {
      type: 'cfo',
      cadence: [{
        phase: 'Month 1',
        title: 'Financial Health Accelerator',
        subtitle: 'Weeks 1–4',
        description: 'Assess financials, identify quick wins, and build a cash plan you can trust.'
      }, {
        phase: 'Ongoing',
        title: 'Monthly CFO Engagement',
        subtitle: 'Continuous',
        description: "A consistent rhythm of reporting, forecasting, and decision support so you're never flying blind."
      }],
      includedModules: ['Reporting & KPI Strategy (budgets, projections, and dashboards owners can use)', 'Cash Flow & Forecasting (13-week cash + rolling forecast)', 'Profitability Intelligence (margins, customer/service profitability)', 'Decision Support (pricing, hiring, spend, growth initiatives)'],
      optionalModules: ['Business Value & Exit Readiness', 'Funding & Capital Planning', 'Acquisition & Investment Modeling'],
      primaryCta: {
        text: 'Schedule a Discovery Call',
        href: 'https://calendar.app.google/QG1eQrZMQfAuXDhF8'
      }
    }
  }, {
    id: 1,
    icon: Briefcase,
    title: 'Deal & Capital Advisory',
    oneLiner: 'M&A, financing, and major investment decisions modeled clearly before you commit capital.',
    outcomes: ['Know the true return and downside before you deploy capital', 'Walk into lender/investor conversations prepared', 'Avoid surprises in diligence and integration'],
    expandedContent: {
      type: 'deal',
      engagementShapes: ['Retainer: ongoing pipeline + negotiation prep', 'Project: single deal/raise/refinance end-to-end'],
      deliverables: ['Base/upside/downside model + sensitivities', 'Diligence red-flag review + working capital view', 'Lender/investor package + capital structure recommendation', '100-day finance plan (post-close/post-funding)'],
      primaryCta: {
        text: 'Book a Deal Readiness Call',
        href: 'https://calendar.app.google/QG1eQrZMQfAuXDhF8'
      }
    }
  }, {
    id: 2,
    icon: Zap,
    badge: 'FIXED SCOPE',
    title: 'Standalone Finance Sprints',
    oneLiner: 'A focused engagement that delivers one clear outcome.',
    outcomes: ['Solve one high-stakes financial blind spot', 'Get a tangible deliverable you can use immediately', 'Try working together before a monthly cadence'],
    expandedContent: {
      type: 'standalone',
      offerings: ['13-week cash flow build', 'KPI dashboard + reporting reset', 'Pricing / margin deep dive', 'Valuation range + value drivers', 'Capital readiness (lender package + covenant planning)'],
      primaryCta: {
        text: 'Explore Standalone Offering Options',
        href: 'https://calendar.app.google/QG1eQrZMQfAuXDhF8'
      }
    }
  }];
  const activeEngagement = engagements[activeCard];
  return <section id="services" className="w-full py-24 sm:py-32 px-6 sm:px-8 bg-white" data-id="element-164">
      <div className="max-w-6xl mx-auto" data-id="element-165">
        {/* Header */}
        <div className="text-center mb-16" data-id="element-166">
          <p className="text-gold-500 text-sm font-semibold tracking-[0.2em] uppercase mb-4" data-id="element-167">
            Engagement Options
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-navy-900 mb-6" data-id="element-168">
            Comprehensive Financial Leadership
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-169">
            From cash flow optimization and ongoing financial support to M&A execution, I provide the strategic finance capabilities typically reserved for large enterprises.
          </p>
        </div>

        {/* Desktop: Card Row */}
        <div className="hidden md:grid md:grid-cols-3 gap-4 mb-6" data-id="element-170">
          {engagements.map((engagement, index) => {
          const Icon = engagement.icon;
          const isActive = activeCard === index;
          return <button key={engagement.id} onClick={() => setActiveCard(index)} className={`relative text-left p-6 rounded-lg border-2 transition-all duration-300 ${isActive ? 'border-gold-500 bg-white shadow-card-hover' : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white'}`} data-id="element-171">
                {/* Badge */}
                {engagement.badge && <span className="absolute top-4 right-4 text-[10px] font-bold tracking-wider uppercase bg-gold-500/10 text-gold-500 px-2 py-1 rounded" data-id="element-172">
                    {engagement.badge}
                  </span>}

                {/* Icon */}
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center mb-4 transition-colors duration-300 ${isActive ? 'bg-gold-500' : 'bg-navy-900'}`} data-id="element-173">
                  <Icon size={22} className="text-white" data-id="element-174" />
                </div>

                {/* Title */}
                <h3 className="text-lg font-serif font-semibold text-navy-900 mb-2 pr-8" data-id="element-175">
                  {engagement.title}
                </h3>

                {/* One-liner */}
                <p className="text-slate-600 text-sm mb-4 leading-relaxed" data-id="element-176">
                  {engagement.oneLiner}
                </p>

                {/* Outcomes */}
                <ul className="space-y-2" data-id="element-177">
                  {engagement.outcomes.map((outcome, idx) => <li key={idx} className="flex items-start text-sm text-slate-700" data-id="element-178">
                      <Check size={14} className="text-gold-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-179" />
                      <span data-id="element-180">{outcome}</span>
                    </li>)}
                </ul>

                {/* Active indicator */}
                {isActive && <motion.div layoutId="activeIndicator" className="absolute -bottom-[2px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-gold-500" data-id="element-181" />}
              </button>;
        })}
        </div>

        {/* Desktop: Expanded Content Panel */}
        <AnimatePresence mode="wait" data-id="element-182">
          <motion.div key={activeCard} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="hidden md:block bg-slate-50 rounded-lg border border-slate-200 p-8" data-id="element-183">
            {activeEngagement.expandedContent.type === 'cfo' && <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-184">
                {/* Left: Cadence */}
                <div data-id="element-185">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-6" data-id="element-186">
                    Engagement Cadence
                  </h4>
                  <div className="space-y-4" data-id="element-187">
                    {activeEngagement.expandedContent.cadence?.map((step, idx) => <div key={idx} className="flex gap-4" data-id="element-188">
                          <div className="flex flex-col items-center" data-id="element-189">
                            <div className="w-10 h-10 rounded-full bg-gold-500 flex items-center justify-center text-navy-900 font-bold text-sm" data-id="element-190">
                              {idx + 1}
                            </div>
                            {idx < (activeEngagement.expandedContent.cadence?.length || 0) - 1 && <div className="w-px h-full bg-gold-500/30 my-2" data-id="element-191" />}
                          </div>
                          <div className="flex-1 pb-4" data-id="element-192">
                            <div className="flex items-center gap-2 mb-1" data-id="element-193">
                              <span className="text-gold-500 text-xs font-semibold uppercase tracking-wider" data-id="element-194">
                                {step.phase}
                              </span>
                              <span className="text-slate-400 text-xs" data-id="element-195">
                                — {step.subtitle}
                              </span>
                            </div>
                            <h5 className="text-navy-900 font-semibold mb-1" data-id="element-196">
                              {step.title}
                            </h5>
                            <p className="text-slate-600 text-sm leading-relaxed" data-id="element-197">
                              {step.description}
                            </p>
                          </div>
                        </div>)}
                  </div>
                </div>

                {/* Right: Modules */}
                <div data-id="element-198">
                  <div className="mb-6" data-id="element-199">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4" data-id="element-200">
                      Included Modules
                    </h4>
                    <ul className="space-y-2" data-id="element-201">
                      {activeEngagement.expandedContent.includedModules?.map((module, idx) => <li key={idx} className="flex items-start text-sm text-slate-700" data-id="element-202">
                            <Check size={14} className="text-emerald-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-203" />
                            <span data-id="element-204">{module}</span>
                          </li>)}
                    </ul>
                  </div>

                  <div className="mb-8" data-id="element-205">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4" data-id="element-206">
                      Optional Modules
                    </h4>
                    <ul className="space-y-2" data-id="element-207">
                      {activeEngagement.expandedContent.optionalModules?.map((module, idx) => <li key={idx} className="flex items-start text-sm text-slate-500" data-id="element-208">
                            <span className="text-gold-500 mr-2" data-id="element-209">+</span>
                            <span data-id="element-210">{module}</span>
                          </li>)}
                    </ul>
                  </div>

                  {/* CTAs */}
                  <div className="flex flex-wrap gap-4" data-id="element-211">
                    <a href={activeEngagement.expandedContent.primaryCta?.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center bg-gold-500 text-navy-900 px-6 py-3 font-semibold rounded hover:bg-gold-400 transition-colors text-sm" data-id="element-212">
                      {activeEngagement.expandedContent.primaryCta?.text}
                      <ArrowRight size={16} className="ml-2" data-id="element-213" />
                    </a>
                    {activeEngagement.expandedContent.secondaryCta && <button onClick={scrollToCaseStudies} className="inline-flex items-center text-navy-900 font-medium text-sm hover:text-gold-500 transition-colors" data-id="element-214">
                        {activeEngagement.expandedContent.secondaryCta.text}
                        <ArrowRight size={14} className="ml-1" data-id="element-215" />
                      </button>}
                  </div>
                </div>
              </div>}

            {activeEngagement.expandedContent.type === 'deal' && <div data-id="element-216">
                {/* Engagement Shapes */}
                <div className="mb-6" data-id="element-217">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4" data-id="element-218">
                    Engagement Shapes
                  </h4>
                  <div className="flex flex-wrap gap-3" data-id="element-219">
                    {activeEngagement.expandedContent.engagementShapes?.map((shape, idx) => <span key={idx} className="bg-navy-900/5 text-navy-900 px-4 py-2 rounded-full text-sm font-medium" data-id="element-220">
                          {shape}
                        </span>)}
                  </div>
                </div>

                {/* Deliverables */}
                <div className="mb-8" data-id="element-221">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4" data-id="element-222">
                    Common Deliverables
                  </h4>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-223">
                    {activeEngagement.expandedContent.deliverables?.map((item, idx) => <li key={idx} className="flex items-start text-sm text-slate-700" data-id="element-224">
                          <Check size={14} className="text-gold-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-225" />
                          <span data-id="element-226">{item}</span>
                        </li>)}
                  </ul>
                </div>

                {/* CTA */}
                <a href={activeEngagement.expandedContent.primaryCta?.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center bg-gold-500 text-navy-900 px-6 py-3 font-semibold rounded hover:bg-gold-400 transition-colors text-sm" data-id="element-227">
                  {activeEngagement.expandedContent.primaryCta?.text}
                  <ArrowRight size={16} className="ml-2" data-id="element-228" />
                </a>
              </div>}

            {activeEngagement.expandedContent.type === 'standalone' && <div data-id="element-229">
                {/* Popular Offerings */}
                <div className="mb-8" data-id="element-230">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4" data-id="element-231">
                    Popular Offerings
                  </h4>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-232">
                    {activeEngagement.expandedContent.offerings?.map((offering, idx) => <li key={idx} className="flex items-center bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-navy-900" data-id="element-233">
                          <Zap size={14} className="text-gold-500 mr-2 flex-shrink-0" data-id="element-234" />
                          <span data-id="element-235">{offering}</span>
                        </li>)}
                  </ul>
                </div>

                {/* CTA */}
                <a href={activeEngagement.expandedContent.primaryCta?.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center bg-gold-500 text-navy-900 px-6 py-3 font-semibold rounded hover:bg-gold-400 transition-colors text-sm" data-id="element-236">
                  {activeEngagement.expandedContent.primaryCta?.text}
                  <ArrowRight size={16} className="ml-2" data-id="element-237" />
                </a>
              </div>}
          </motion.div>
        </AnimatePresence>

        {/* Mobile: Accordion Stack */}
        <div className="md:hidden space-y-4" data-id="element-238">
          {engagements.map((engagement, index) => {
          const Icon = engagement.icon;
          const isActive = activeCard === index;
          return <div key={engagement.id} className="border border-slate-200 rounded-lg overflow-hidden" data-id="element-239">
                {/* Accordion Header */}
                <button onClick={() => setActiveCard(isActive ? -1 : index)} className={`w-full text-left p-5 transition-colors ${isActive ? 'bg-white' : 'bg-slate-50'}`} data-id="element-240">
                  <div className="flex items-start justify-between" data-id="element-241">
                    <div className="flex items-start gap-4" data-id="element-242">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-gold-500' : 'bg-navy-900'}`} data-id="element-243">
                        <Icon size={20} className="text-white" data-id="element-244" />
                      </div>
                      <div data-id="element-245">
                        {engagement.badge && <span className="inline-block text-[10px] font-bold tracking-wider uppercase bg-gold-500/10 text-gold-500 px-2 py-0.5 rounded mb-2" data-id="element-246">
                            {engagement.badge}
                          </span>}
                        <h3 className="text-base font-serif font-semibold text-navy-900 mb-1" data-id="element-247">
                          {engagement.title}
                        </h3>
                        <p className="text-slate-600 text-sm leading-relaxed" data-id="element-248">
                          {engagement.oneLiner}
                        </p>
                      </div>
                    </div>
                    <ChevronDown size={20} className={`text-slate-400 flex-shrink-0 ml-2 transition-transform ${isActive ? 'rotate-180' : ''}`} data-id="element-249" />
                  </div>

                  {/* Outcomes (always visible) */}
                  <ul className="mt-4 space-y-2 pl-14" data-id="element-250">
                    {engagement.outcomes.map((outcome, idx) => <li key={idx} className="flex items-start text-sm text-slate-700" data-id="element-251">
                        <Check size={14} className="text-gold-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-252" />
                        <span data-id="element-253">{outcome}</span>
                      </li>)}
                  </ul>
                </button>

                {/* Accordion Content */}
                <AnimatePresence data-id="element-254">
                  {isActive && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25
              }} className="overflow-hidden" data-id="element-255">
                      <div className="p-5 pt-0 border-t border-slate-100" data-id="element-256">
                        {engagement.expandedContent.type === 'cfo' && <div className="space-y-6" data-id="element-257">
                            {/* Cadence */}
                            <div data-id="element-258">
                              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4" data-id="element-259">
                                Engagement Cadence
                              </h4>
                              <div className="space-y-4" data-id="element-260">
                                {engagement.expandedContent.cadence?.map((step, idx) => <div key={idx} className="bg-slate-50 rounded-lg p-4" data-id="element-261">
                                      <div className="flex items-center gap-2 mb-1" data-id="element-262">
                                        <span className="text-gold-500 text-xs font-semibold uppercase" data-id="element-263">
                                          {step.phase}
                                        </span>
                                        <span className="text-slate-400 text-xs" data-id="element-264">
                                          — {step.subtitle}
                                        </span>
                                      </div>
                                      <h5 className="text-navy-900 font-semibold text-sm mb-1" data-id="element-265">
                                        {step.title}
                                      </h5>
                                      <p className="text-slate-600 text-sm" data-id="element-266">
                                        {step.description}
                                      </p>
                                    </div>)}
                              </div>
                            </div>

                            {/* Modules */}
                            <div data-id="element-267">
                              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-268">
                                Included Modules
                              </h4>
                              <ul className="space-y-2 mb-4" data-id="element-269">
                                {engagement.expandedContent.includedModules?.map((module, idx) => <li key={idx} className="flex items-start text-sm text-slate-700" data-id="element-270">
                                      <Check size={14} className="text-emerald-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-271" />
                                      <span data-id="element-272">{module}</span>
                                    </li>)}
                              </ul>

                              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-273">
                                Optional Modules
                              </h4>
                              <ul className="space-y-2" data-id="element-274">
                                {engagement.expandedContent.optionalModules?.map((module, idx) => <li key={idx} className="flex items-start text-sm text-slate-500" data-id="element-275">
                                      <span className="text-gold-500 mr-2" data-id="element-276">
                                        +
                                      </span>
                                      <span data-id="element-277">{module}</span>
                                    </li>)}
                              </ul>
                            </div>

                            {/* CTAs */}
                            <div className="flex flex-col gap-3" data-id="element-278">
                              <a href={engagement.expandedContent.primaryCta?.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-gold-500 text-navy-900 px-6 py-3 font-semibold rounded hover:bg-gold-400 transition-colors text-sm" data-id="element-279">
                                {engagement.expandedContent.primaryCta?.text}
                                <ArrowRight size={16} className="ml-2" data-id="element-280" />
                              </a>
                              {engagement.expandedContent.secondaryCta && <button onClick={scrollToCaseStudies} className="inline-flex items-center justify-center text-navy-900 font-medium text-sm hover:text-gold-500 transition-colors" data-id="element-281">
                                  {engagement.expandedContent.secondaryCta.text}
                                  <ArrowRight size={14} className="ml-1" data-id="element-282" />
                                </button>}
                            </div>
                          </div>}

                        {engagement.expandedContent.type === 'deal' && <div className="space-y-6" data-id="element-283">
                            <div data-id="element-284">
                              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-285">
                                Engagement Shapes
                              </h4>
                              <div className="flex flex-col gap-2" data-id="element-286">
                                {engagement.expandedContent.engagementShapes?.map((shape, idx) => <span key={idx} className="bg-navy-900/5 text-navy-900 px-4 py-2 rounded-lg text-sm" data-id="element-287">
                                      {shape}
                                    </span>)}
                              </div>
                            </div>

                            <div data-id="element-288">
                              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-289">
                                Common Deliverables
                              </h4>
                              <ul className="space-y-2" data-id="element-290">
                                {engagement.expandedContent.deliverables?.map((item, idx) => <li key={idx} className="flex items-start text-sm text-slate-700" data-id="element-291">
                                      <Check size={14} className="text-gold-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-292" />
                                      <span data-id="element-293">{item}</span>
                                    </li>)}
                              </ul>
                            </div>

                            <a href={engagement.expandedContent.primaryCta?.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-full bg-gold-500 text-navy-900 px-6 py-3 font-semibold rounded hover:bg-gold-400 transition-colors text-sm" data-id="element-294">
                              {engagement.expandedContent.primaryCta?.text}
                              <ArrowRight size={16} className="ml-2" data-id="element-295" />
                            </a>
                          </div>}

                        {engagement.expandedContent.type === 'standalone' && <div className="space-y-6" data-id="element-296">
                            <div data-id="element-297">
                              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-298">
                                Popular Offerings
                              </h4>
                              <ul className="space-y-2" data-id="element-299">
                                {engagement.expandedContent.offerings?.map((offering, idx) => <li key={idx} className="flex items-center bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-navy-900" data-id="element-300">
                                      <Zap size={14} className="text-gold-500 mr-2 flex-shrink-0" data-id="element-301" />
                                      <span data-id="element-302">{offering}</span>
                                    </li>)}
                              </ul>
                            </div>

                            <a href={engagement.expandedContent.primaryCta?.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-full bg-gold-500 text-navy-900 px-6 py-3 font-semibold rounded hover:bg-gold-400 transition-colors text-sm" data-id="element-303">
                              {engagement.expandedContent.primaryCta?.text}
                              <ArrowRight size={16} className="ml-2" data-id="element-304" />
                            </a>
                          </div>}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>
    </section>;
}